import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useFlowstateAuth } from '../../context/AuthContext';

export const TrackCard = ({ id, track, isLight }) => {
    const navigate = useNavigate();
    const { user } = useFlowstateAuth();

    return (
        <Paper
            elevation={0}
            onClick={() => navigate(`/interview-prep/${id}`)}
            sx={{
                p: 5,
                borderRadius: 6,
                background: isLight ? 'rgba(255, 255, 255, 0.7)' : 'rgba(17, 17, 19, 0.6)',
                backdropFilter: 'blur(10px)',
                border: isLight ? '1px solid rgba(0, 0, 0, 0.05)' : '1px solid rgba(255, 255, 255, 0.05)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                minHeight: '420px',
                '&:hover': {
                    transform: 'translateY(-12px)',
                    background: isLight ? 'rgba(255, 255, 255, 1)' : 'rgba(20, 20, 24, 0.95)',
                    boxShadow: isLight
                        ? `0 30px 60px -12px rgba(0,0,0,0.1), 0 0 40px ${track.color}15`
                        : `0 30px 60px -12px rgba(0,0,0,0.7), 0 0 40px ${track.color}15`,
                    '& .track-icon-wrapper': {
                        transform: 'scale(1.1) rotate(-5deg)',
                        bgcolor: `${track.color}20`,
                        color: track.color
                    },
                    '& .track-arrow': {
                        transform: 'translateX(5px)',
                        opacity: 1
                    },
                    '& .explore-button': {
                        color: track.color,
                        transform: 'translateX(2px)'
                    }
                }
            }}
        >
            {/* Background Glow */}
            <Box sx={{
                position: 'absolute',
                top: '-20%',
                right: '-10%',
                width: '60%',
                height: '60%',
                background: `radial-gradient(circle, ${track.color}10 0%, transparent 70%)`,
                zIndex: 0,
                pointerEvents: 'none'
            }} />

            {/* Track Icon */}
            <Box
                className="track-icon-wrapper"
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isLight ? '#f1f5f9' : '#0a0a0c',
                    color: isLight ? '#64748b' : '#a1a1aa',
                    mb: 4,
                    border: isLight ? '1px solid #e2e8f0' : '1px solid #27272a',
                    transition: 'all 0.4s ease',
                    zIndex: 1,
                    '& svg': { fontSize: 40 }
                }}
            >
                {track.icon}
            </Box>

            <Box sx={{ zIndex: 1, flex: 1 }}>
                <Typography variant="h5" fontWeight="800" sx={{
                    mb: 1.5,
                    fontSize: '1.4rem',
                    letterSpacing: '-0.5px',
                    color: isLight ? '#1e293b' : '#f8fafc'
                }}>
                    {track.title}
                </Typography>

                <Typography variant="body2" sx={{
                    color: isLight ? '#64748b' : '#94a3b8',
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    mb: 4
                }}>
                    {track.description}
                </Typography>
            </Box>

            {/* Bottom Stats/Info */}
            <Box sx={{
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pt: 3,
                borderTop: isLight ? '1px solid #e2e8f0' : '1px solid #27272a'
            }}>
                <Typography variant="caption" sx={{
                    color: track.color,
                    fontWeight: 800,
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                }}>
                    {track.categories.every(catId => user?.purchasedModules?.includes(catId)) ? (
                        <Box component="span" sx={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            UNLOCKED
                        </Box>
                    ) : `${track.categories.length} Modules`}
                </Typography>

                <Box className="explore-button" sx={{ display: 'flex', alignItems: 'center', gap: 1, transition: 'all 0.3s ease' }}>
                    <Typography variant="button" sx={{
                        fontWeight: 800,
                        color: 'inherit',
                        fontSize: '0.8rem'
                    }}>
                        EXPLORE
                    </Typography>
                    <ArrowForwardIcon className="track-arrow" sx={{
                        fontSize: 18,
                        opacity: 0.6,
                        transition: 'all 0.3s ease',
                        color: 'inherit'
                    }} />
                </Box>
            </Box>
        </Paper>
    );
};
